import { cn } from '@ecom/helper-react-class-combine';
import * as React from 'react';
import { Link } from '@/shared/components/primitives/link';
import { Image } from '@/shared/components/image';
import { DisplayPrice } from '@/shared/components/display-price';
import { PriceExcludingWildMembership, PriceIncludingWildMembership } from '@/shared/types/prices';
import { GameCardUIRating } from '@/features/games/shared/components/game-card-ui-rating';

interface ImageType {
  altText: string;
  src: string;
}

interface Product {
  id: string;
  name: string;
  href: string;
  image?: ImageType;
  price?: PriceExcludingWildMembership;
  promotionPrice?: PriceIncludingWildMembership;
  subtitle?: string;
  badge?: string;
}

type OverlayConfig = {
  content?: React.ReactNode;
  ariaLabel?: string;
};

interface Props extends Product {
  imageSize?: 'square' | 'tall' | 'wide' | 'hero';
  categories?: Array<{ name?: string; path?: string }>;
  sizes?: string;
  overlay?: OverlayConfig;
  hideCategories?: boolean;
  onSale?: boolean;
  rating?: number;
  ratingSizePx?: number;
  isPriceLoading?: boolean;
  freePriceCaption?: string;
  /** ADR-018: `full` in carousels, `hover` in grids and lists. */
  prefetchMode: 'full' | 'hover';
}

export const GameCardUI = ({
  image,
  imageSize,
  href,
  price,
  promotionPrice,
  id,
  subtitle,
  name,
  categories,
  sizes = '300px',
  overlay,
  hideCategories = !!overlay,
  onSale,
  rating,
  isPriceLoading = false,
  freePriceCaption,
  prefetchMode,
  ...props
}: Props) => {
  const isHero = imageSize === 'hero';
  const seenCategories = new Set<string>();
  const uniqueCategories = categories?.filter((category) => {
    const key = `${category.name}::${category.path}`;

    if (seenCategories.has(key)) {
      return false;
    }

    seenCategories.add(key);
    return true;
  });

  return (
    <div
      className={cn(
        'group relative flex w-full flex-col overflow-visible',
        overlay && 'h-full transition-transform',
        freePriceCaption && 'h-full',
      )}
      key={`card-container-${id}`}
      {...props}
    >
      <div
        className={cn(
          'relative flex flex-1 flex-col',
          overlay &&
            'transform-gpu transition-all duration-300 ease-out group-focus-within:scale-[0.98] group-focus-within:blur-[2px] group-hover:scale-[0.98] group-hover:blur-[2px] motion-reduce:transform-none',
        )}
      >
        <div className='relative flex justify-center'>
          <div
            className={cn('relative flex-auto overflow-hidden', {
              'aspect-square': imageSize === 'square',
              'aspect-[4/5]': imageSize === 'tall',
              'aspect-[7/5]': imageSize === 'wide',
              'aspect-[16/6]': isHero,
            })}
          >
            {(onSale || promotionPrice) && (
              <span
                aria-label='On Sale'
                className={cn(
                  'pointer-events-none absolute top-0 left-0 z-10',
                  onSale
                    ? 'translate-y-[50%] text-[11px] font-semibold tracking-wide'
                    : 'translate-y-[60%] text-[9px] font-medium tracking-normal',
                  'h-7 w-[180px] -translate-x-[30%] -rotate-45',
                  'flex items-center justify-center bg-blue-600 text-white uppercase',
                  'shadow-md',
                )}
              >
                {onSale ? 'On Sale!' : 'Special Offer!'}
              </span>
            )}

            {image ? (
              <>
                {!overlay ? (
                  // Full prefetch on the title link only: two `prefetch={true}` links to one URL
                  // prefetch it twice, and a click here still finds it in the client cache.
                  <Link
                    className='relative block h-full w-full text-white hover:text-gray-400 focus:outline-hidden'
                    href={href}
                    prefetchOnHover={prefetchMode === 'hover'}
                  >
                    <Image
                      alt={image.altText}
                      className={cn(isHero ? 'object-cover' : 'object-contain')}
                      {...(isHero ? { width: 1600, height: 600 } : { fill: true })}
                      src={image.src}
                      sizes={sizes}
                    />
                  </Link>
                ) : (
                  <Image
                    alt={image.altText}
                    className={cn(isHero ? 'object-cover' : 'object-contain')}
                    {...(isHero ? { width: 1600, height: 600 } : { fill: true })}
                    src={image.src}
                    sizes={sizes}
                  />
                )}
              </>
            ) : (
              <div className='h-full w-full bg-gray-200' />
            )}
          </div>
        </div>

        <div
          className='mt-3 mb-2 flex h-full flex-1 flex-col items-center gap-1 px-2'
          key={`card-content-${id}`}
        >
          {subtitle ? <p className='text-text'>{subtitle}</p> : null}

          <h3 className='mb-2 w-full text-center text-lg font-semibold'>
            {!overlay ? (
              <Link
                className='text-text block w-full transition-colors hover:text-gray-400'
                href={href}
                prefetch={prefetchMode === 'full' || undefined}
                prefetchOnHover={prefetchMode === 'hover'}
              >
                {name}
              </Link>
            ) : (
              <>{name}</>
            )}
          </h3>

          {!hideCategories && uniqueCategories
            ? uniqueCategories.map((category, index) => (
                <div className='text-center text-[16px]' key={`cat-${id}-${index}`}>
                  <Link className='text-text-muted' href={category.path || ''} prefetch={false}>
                    {category?.name}
                  </Link>
                </div>
              ))
            : null}

          <div
            className={cn(
              'flex min-h-[1.75rem] w-full justify-center pt-1',
              freePriceCaption && 'mt-auto',
            )}
          >
            <CardPrice
              price={price}
              promotionPrice={promotionPrice}
              isPriceLoading={isPriceLoading}
              freePriceCaption={freePriceCaption}
            />
          </div>

          {typeof rating === 'number' && (
            <GameCardUIRating value={rating} className='mb-2' showValueText={false} />
          )}
        </div>
      </div>

      {overlay?.content && (
        <div
          aria-label={overlay.ariaLabel}
          className={cn(
            'pointer-events-none absolute inset-0 flex items-center justify-center',
            'bg-black/40 backdrop-blur-[2px]',
            'opacity-0 transition-opacity duration-300',
            'group-focus-within:opacity-100 group-hover:opacity-100',
          )}
        >
          <div className='pointer-events-auto mx-6 text-center'>{overlay.content}</div>
        </div>
      )}
    </div>
  );
};

GameCardUI.displayName = 'ProductCard';

function CardPrice({
  price,
  promotionPrice,
  isPriceLoading,
  freePriceCaption,
}: Pick<Props, 'price' | 'promotionPrice' | 'isPriceLoading' | 'freePriceCaption'>) {
  if (isPriceLoading) {
    return (
      <span className='bg-background-panel mx-4 mb-2 h-6 w-20 animate-pulse rounded-md'></span>
    );
  }
  if (!price) return null;

  if (freePriceCaption) {
    return (
      <div className='mb-2 flex flex-col items-center'>
        <span className='flex items-baseline gap-2'>
          <DisplayPrice
            value={price.value}
            currencyCode={price.currencyCode}
            className='text-text-muted line-through'
          />
          <DisplayPrice
            value={0}
            currencyCode={price.currencyCode}
            fractionDigits={0}
            className='text-primary text-xl font-bold'
          />
        </span>
        <span className='text-text-muted text-sm'>{freePriceCaption}</span>
      </div>
    );
  }

  return (
    <div className='text-primary mb-2 flex w-full flex-col items-center gap-1'>
      {price.type === 'sale' || promotionPrice ? (
        <div className='flex flex-col items-center'>
          <span className='text-primary font-semibold'>
            <span className='inline-flex items-baseline'>
              <DisplayPrice
                value={price.type === 'sale' ? price.value : promotionPrice!.value}
                currencyCode={price.currencyCode}
              />
              {price.type !== 'sale' && promotionPrice ? (
                <span aria-label='Promotional price'>*</span>
              ) : null}
            </span>
          </span>

          <span className='line-through opacity-70'>
            <DisplayPrice
              value={price.type === 'sale' ? price.previousValue! : price.value}
              currencyCode={price.currencyCode}
            />
          </span>
        </div>
      ) : (
        <span className='text-primary font-semibold'>
          <DisplayPrice value={price.value} currencyCode={price.currencyCode} />
        </span>
      )}
    </div>
  );
}
